/**
 * Mirrors Services/BillingService.cs exactly — same rounding, same order of
 * operations — so the total shown at the counter before checkout is the
 * total the server actually charges. A mismatch here means either a
 * surprised customer or a bill that fails to post.
 */

export interface CartLine {
  medicineId: string;
  name: string;
  shortName?: string;
  price: number;
  unit: string;
  maxStock: number;
  batchNo: string;
  daysToExpiry: number;
  gstRate: number;
  qty: number;
}

export interface GstBreakdownEntry {
  rate: number;
  amount: number;
}

export interface BillTotals {
  subtotal: number;
  discount: number;
  gstAmount: number;
  gstBreakdown: GstBreakdownEntry[];
  roundOff: number;
  grandTotal: number;
}

/** Round half away from zero, matching C#'s MidpointRounding.AwayFromZero. */
function round2(value: number): number {
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(value) * 100)) / 100;
}

export function computeBillTotals(
  cart: CartLine[],
  discountInput: number,
  roundOffEnabled: boolean,
): BillTotals {
  const subtotal = round2(cart.reduce((sum, line) => sum + line.qty * line.price, 0));

  // Never discount more than the bill is worth, and never go negative.
  const discount = Math.min(Math.max(0, discountInput), subtotal);
  const discountFactor = subtotal > 0 ? discount / subtotal : 0;

  let gstAmount = 0;
  const byRate = new Map<number, number>();

  for (const line of cart) {
    const lineSubtotal = round2(line.qty * line.price);
    const lineDiscount = round2(lineSubtotal * discountFactor);
    const lineNet = round2(lineSubtotal - lineDiscount);
    const lineGst = round2((lineNet * line.gstRate) / 100);

    gstAmount += lineGst;
    if (lineGst > 0) {
      byRate.set(line.gstRate, (byRate.get(line.gstRate) ?? 0) + lineGst);
    }
  }

  gstAmount = round2(gstAmount);

  const rawTotal = subtotal - discount + gstAmount;
  const grandTotal = roundOffEnabled ? Math.round(rawTotal) : round2(rawTotal);
  const roundOff = roundOffEnabled ? round2(grandTotal - rawTotal) : 0;

  return {
    subtotal,
    discount,
    gstAmount,
    gstBreakdown: [...byRate.entries()]
      .map(([rate, amount]) => ({ rate, amount: round2(amount) }))
      .sort((a, b) => a.rate - b.rate),
    roundOff,
    grandTotal,
  };
}
